import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './OutreachTracker.css';

function OutreachTracker({ user }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCallModal, setShowCallModal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [newLead, setNewLead] = useState({
    seller_name: '', seller_phone: '', seller_email: '',
    property_address: '', property_city: '', property_state: '',
    property_zip: '', asking_price: '', source: 'zillow_fsbo'
  });

  const [callData, setCallData] = useState({
    call_status: 'called', call_notes: '', follow_up_date: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get('/api/outreach/leads', { headers }),
        axios.get('/api/outreach/stats', { headers })
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching outreach data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/outreach/leads', newLead, { headers });
      setNewLead({
        seller_name: '', seller_phone: '', seller_email: '',
        property_address: '', property_city: '', property_state: '',
        property_zip: '', asking_price: '', source: 'zillow_fsbo'
      });
      setShowAddForm(false);
      fetchLeads();
    } catch (error) {
      console.error('Error adding lead:', error);
    }
  };

  const handleLogCall = async (leadId) => {
    try {
      await axios.put(`/api/outreach/leads/${leadId}/call`, callData, { headers });
      setShowCallModal(null);
      setCallData({ call_status: 'called', call_notes: '', follow_up_date: '' });
      fetchLeads();
    } catch (error) {
      console.error('Error logging call:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`/api/outreach/leads/${leadId}`, { headers });
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesFilter = filter === 'all'
      || (filter === 'not_called' && lead.call_status === 'not_called')
      || (filter === 'called' && lead.call_status === 'called')
      || (filter === 'pitched' && lead.call_status === 'pitched')
      || (filter === 'follow_up' && lead.call_status === 'follow_up')
      || (filter === 'activated' && lead.activated === 1)
      || (filter === 'not_interested' && lead.call_status === 'not_interested');

    const matchesSearch = !searchTerm ||
      (lead.property_address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.seller_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.property_city || '').toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getStatusInfo = (status) => {
    const map = {
      not_called: { bg: '#f4f4f5', color: '#71717a', label: 'Not Called' },
      called: { bg: '#fff', color: '#18181b', border: '#d4d4d8', label: 'Called' },
      pitched: { bg: '#18181b', color: '#fff', label: 'Pitched' },
      follow_up: { bg: '#7c3aed', color: '#fff', label: 'Follow Up' },
      activated: { bg: '#059669', color: '#fff', label: 'Activated' },
      not_interested: { bg: '#fff', color: '#a1a1aa', border: '#e4e4e7', label: 'Pass' }
    };
    return map[status] || map.not_called;
  };

  const formatPrice = (p) => p ? '$' + parseFloat(p).toLocaleString() : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  if (loading) {
    return <div className="outreach-page"><div className="outreach-container"><p style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>Loading...</p></div></div>;
  }

  return (
    <div className="outreach-page">
      <div className="outreach-container">
        <div className="outreach-header">
          <div>
            <h1>FSBO Outreach</h1>
            <p>Track your cold calls and conversions</p>
          </div>
          <button className="btn-add-lead" onClick={() => setShowAddForm(true)}>+ Add Lead</button>
        </div>

        {stats && (
          <div className="outreach-stats">
            {[
              { n: stats.total, l: 'Leads' },
              { n: stats.called, l: 'Called' },
              { n: stats.activated, l: 'Activated' },
              { n: stats.closed, l: 'Closed' },
            ].map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-number">{s.n}</div>
                <div className="stat-label">{s.l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="outreach-filters">
          <div className="filter-tabs">
            {[
              { id: 'all', label: 'All', count: leads.length },
              { id: 'not_called', label: 'Not Called', count: leads.filter(l => l.call_status === 'not_called').length },
              { id: 'follow_up', label: 'Follow Up', count: leads.filter(l => l.call_status === 'follow_up').length },
              { id: 'activated', label: 'Activated', count: leads.filter(l => l.activated === 1).length },
            ].map(tab => (
              <button
                key={tab.id}
                className={`filter-tab ${filter === tab.id ? 'active' : ''}`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="outreach-table-wrapper">
          <table className="outreach-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Seller</th>
                <th>Price</th>
                <th>Source</th>
                <th>Status</th>
                <th>Last Call</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr><td colSpan="8" className="empty-state">{leads.length === 0 ? 'No leads yet. Add your first FSBO lead.' : 'No leads match your filter.'}</td></tr>
              ) : filteredLeads.map(lead => {
                const si = getStatusInfo(lead.call_status);
                return (
                  <tr key={lead.id} className={lead.call_status === 'not_interested' ? 'row-dimmed' : ''}>
                    <td className="cell-property">
                      <div className="property-main">{lead.property_address}</div>
                      <div className="property-sub">{[lead.property_city, lead.property_state, lead.property_zip].filter(Boolean).join(', ')}</div>
                    </td>
                    <td className="cell-seller">
                      <div className="seller-name">{lead.seller_name || '—'}</div>
                      {lead.seller_phone && <div className="seller-phone">{lead.seller_phone}</div>}
                    </td>
                    <td className="cell-price">{formatPrice(lead.asking_price)}</td>
                    <td><span className="source-badge">{lead.source === 'zillow_fsbo' ? 'Zillow' : lead.source}</span></td>
                    <td>
                      <span className="status-badge" style={{ background: si.bg, color: si.color, border: si.border ? `1px solid ${si.border}` : '1px solid transparent' }}>
                        {si.label}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(lead.call_date)}</td>
                    <td className="cell-notes">
                      <div className="notes-preview" title={lead.call_notes || ''}>{lead.call_notes ? (lead.call_notes.length > 35 ? lead.call_notes.substring(0, 35) + '...' : lead.call_notes) : '—'}</div>
                      {lead.follow_up_date && <div className="follow-up-tag">F/U {formatDate(lead.follow_up_date)}</div>}
                    </td>
                    <td className="cell-actions">
                      <button className="action-btn" onClick={() => { setShowCallModal(lead); setCallData({ call_status: lead.call_status === 'not_called' ? 'called' : lead.call_status, call_notes: lead.call_notes || '', follow_up_date: '' }); }} title="Log Call">📞</button>
                      <button className="action-btn del" onClick={() => handleDeleteLead(lead.id)} title="Delete">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add Lead Modal */}
        {showAddForm && (
          <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add FSBO Lead</h2>
                <button className="modal-close" onClick={() => setShowAddForm(false)}>✕</button>
              </div>
              <form onSubmit={handleAddLead}>
                <div className="form-section">
                  <h3>Property</h3>
                  <div className="form-row">
                    <div className="form-group full">
                      <label>Address *</label>
                      <input type="text" value={newLead.property_address} onChange={e => setNewLead({ ...newLead, property_address: e.target.value })} required placeholder="123 Main St" maxLength={100} />
                    </div>
                  </div>
                  <div className="form-row three-col">
                    <div className="form-group"><label>City</label><input type="text" value={newLead.property_city} onChange={e => setNewLead({ ...newLead, property_city: e.target.value })} placeholder="Houston" maxLength={50} /></div>
                    <div className="form-group"><label>State</label><input type="text" value={newLead.property_state} onChange={e => setNewLead({ ...newLead, property_state: e.target.value.toUpperCase() })} placeholder="TX" maxLength={2} style={{ textTransform: 'uppercase' }} /></div>
                    <div className="form-group"><label>ZIP</label><input type="text" value={newLead.property_zip} onChange={e => { const v = e.target.value.replace(/\D/g, ''); setNewLead({ ...newLead, property_zip: v }); }} placeholder="77001" maxLength={5} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Asking Price</label><input type="text" value={newLead.asking_price} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setNewLead({ ...newLead, asking_price: v }); }} placeholder="350,000" maxLength={12} /></div>
                    <div className="form-group">
                      <label>Source</label>
                      <select value={newLead.source} onChange={e => setNewLead({ ...newLead, source: e.target.value })}>
                        <option value="zillow_fsbo">Zillow FSBO</option>
                        <option value="facebook">Facebook</option>
                        <option value="craigslist">Craigslist</option>
                        <option value="yard_sign">Yard Sign</option>
                        <option value="referral">Referral</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-section">
                  <h3>Seller Info</h3>
                  <div className="form-row">
                    <div className="form-group"><label>Name</label><input type="text" value={newLead.seller_name} onChange={e => setNewLead({ ...newLead, seller_name: e.target.value })} placeholder="John Smith" maxLength={60} /></div>
                    <div className="form-group"><label>Phone</label><input type="tel" value={newLead.seller_phone} onChange={e => { let v = e.target.value.replace(/\D/g, ''); if (v.length > 10) v = v.slice(0, 10); if (v.length >= 7) v = `(${v.slice(0,3)}) ${v.slice(3,6)}-${v.slice(6)}`; else if (v.length >= 4) v = `(${v.slice(0,3)}) ${v.slice(3)}`; else if (v.length > 0) v = `(${v}`; setNewLead({ ...newLead, seller_phone: v }); }} placeholder="(555) 123-4567" maxLength={14} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group full"><label>Email</label><input type="email" value={newLead.seller_email} onChange={e => setNewLead({ ...newLead, seller_email: e.target.value })} placeholder="seller@email.com" maxLength={80} /></div>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
                  <button type="submit" className="btn-submit">Add Lead</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Call Log Modal */}
        {showCallModal && (
          <div className="modal-overlay" onClick={() => setShowCallModal(null)}>
            <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Log Call</h2>
                <button className="modal-close" onClick={() => setShowCallModal(null)}>✕</button>
              </div>
              <div className="call-lead-info">
                <strong>{showCallModal.property_address}</strong>
                <span>{showCallModal.seller_name || 'Unknown'} — {showCallModal.seller_phone || 'No phone'}</span>
              </div>
              <div className="form-section">
                <div className="form-group">
                  <label>Call Result</label>
                  <div className="status-options">
                    {[
                      { value: 'called', label: 'No Answer' },
                      { value: 'pitched', label: 'Pitched' },
                      { value: 'follow_up', label: 'Follow Up' },
                      { value: 'activated', label: 'Activated ✓' },
                      { value: 'not_interested', label: 'Not Interested' }
                    ].map(opt => (
                      <button key={opt.value} type="button" className={`status-option ${callData.call_status === opt.value ? 'selected' : ''}`} onClick={() => setCallData({ ...callData, call_status: opt.value })}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea value={callData.call_notes} onChange={e => setCallData({ ...callData, call_notes: e.target.value })} placeholder="What happened on the call?" rows={3} />
                </div>
                {callData.call_status === 'follow_up' && (
                  <div className="form-group">
                    <label>Follow Up Date</label>
                    <input type="date" value={callData.follow_up_date} onChange={e => setCallData({ ...callData, follow_up_date: e.target.value })} />
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCallModal(null)}>Cancel</button>
                <button type="button" className="btn-submit" onClick={() => handleLogCall(showCallModal.id)}>Save Call</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OutreachTracker;
